import { message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";

import { getShopDetail, updateShop } from "@/api/stores";
import type { ShopDetailVO } from "@/api/stores/type";

import { buildShopUpdatePayload } from "./buildShopUpdatePayload";

/**
 * 拉取门店详情
 * @param shopId 门店 ID
 * @param open 弹窗是否打开
 */
export const useShopDetail = (shopId?: string | null, open = false) => {
  // 门店详情数据
  const [shop, setShop] = useState<ShopDetailVO | null>(null);
  // 详情加载中
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  /** 按 ID 请求详情 */
  const loadDetail = useCallback(async (id: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await getShopDetail(id);
      if (requestId !== requestIdRef.current) return;
      setShop(res.data);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setShop(null);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!open || !shopId) return;
    void loadDetail(shopId);
  }, [loadDetail, open, shopId]);

  /** 关闭弹窗时清空，避免下次打开闪旧数据 */
  useEffect(() => {
    if (open) return;
    requestIdRef.current += 1;
    setShop(null);
    setLoading(false);
  }, [open]);

  /** 行内编辑单字段并刷新详情 */
  const editShop = useCallback(
    async (field: string, value: string) => {
      if (!shopId) return;
      try {
        const payload = buildShopUpdatePayload(field, value);
        const res = await updateShop(shopId, payload);
        setShop(res.data);
        message.success("保存成功");
      } catch {
        // 错误提示由请求拦截器统一处理
      }
    },
    [shopId],
  );

  return {
    shop,
    loading,
    editShop,
  };
};
